import React, { useState, useEffect } from "react";
import { Form, Button, Container, Row, Col, Alert, Spinner, Card } from "react-bootstrap";
import { useNavigate, Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const GroupDetails = () => {
    const navigate = useNavigate();
    const [primaryVisitor, setPrimaryVisitor] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [serverMsg, setServerMsg] = useState("");
    const [errors, setErrors] = useState({});
    const [groupMembers, setGroupMembers] = useState([]);
    const [adultCount, setAdultCount] = useState("");

    // Thank You screen states
    const [showThankYou, setShowThankYou] = useState(false);
    const [registeredGroupId, setRegisteredGroupId] = useState("");
    const [registeredVisitorId, setRegisteredVisitorId] = useState("");
    const [registeredName, setRegisteredName] = useState("");

    useEffect(() => {
        const stored = sessionStorage.getItem("tmgf_primary_visitor");
        if (!stored) {
            toast.warn("Please complete the primary visitor registration details first.");
            navigate("/visitor/register");
            return;
        }
        try {
            const visitor = JSON.parse(stored);
            setPrimaryVisitor(visitor);
            if (!visitor.fullName && (visitor.identifier || visitor.mobileNumber)) {
                const searchId = visitor.identifier || visitor.mobileNumber;
                fetch(`${BASE_URL}/api/visitors/lookup`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ identifier: searchId })
                })
                    .then(res => res.json())
                    .then(data => {
                        if (data && data.fullName) {
                            setPrimaryVisitor(prev => ({ ...prev, fullName: data.fullName }));
                        }
                    })
                    .catch(err => console.error("Error looking up primary visitor name:", err));
            }
        } catch (e) {
            navigate("/visitor/register");
        }
    }, [navigate]);

    const checkGroupMemberPhone = async (id, phone, index) => {
        if (!/^[6-9]\d{9}$/.test(phone)) return;
        try {
            const response = await fetch(`${BASE_URL}/api/visitors/lookup`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ identifier: phone })
            });
            const data = await response.json();
            if (response.status === 404) {
                toast.info(`Member #${index + 1}: Seems like a new visitor! Please enter their name.`);
                setGroupMembers(prev =>
                    prev.map(m => m.id === id ? { ...m, hasVisited: false, lookupDone: true, name: "" } : m)
                );
            } else if (response.ok && data.fullName) {
                setGroupMembers(prev =>
                    prev.map(m => m.id === id ? { ...m, name: data.fullName, hasVisited: true, lookupDone: true } : m)
                );
                toast.success(`Member #${index + 1}: Found existing profile for '${data.fullName}'`);
            }
        } catch (err) {
            console.error("Error looking up group member phone:", err);
        }
    };

    const updateMember = (id, field, value) => {
        setGroupMembers(prev => prev.map(m => m.id === id ? { ...m, [field]: value } : m));
    };

    const removeMember = (id) => {
        setGroupMembers(prev => {
            const filtered = prev.filter(m => m.id !== id);
            setAdultCount(String(filtered.length));
            return filtered;
        });
    };

    const validateForm = () => {
        const newErrors = {};
        const expectedAdults = parseInt(adultCount) || 0;
        const adultMembers = groupMembers.filter(m => m.type === "adult");
        if (expectedAdults > 0 && adultMembers.length !== expectedAdults) {
            newErrors.adultCount = `You entered ${expectedAdults} adults but added ${adultMembers.length} member(s). Please match exactly.`;
        }
        groupMembers.forEach((m, i) => {
            if (m.type === "adult") {
                if (!m.phone) newErrors[`member_phone_${i}`] = "Phone number is required";
                else if (m.phone.length !== 10) newErrors[`member_phone_${i}`] = "Must be 10 digits";
            }
            if (m.type === "child" || (m.lookupDone && !m.hasVisited)) {
                if (!m.name.trim()) newErrors[`member_name_${i}`] = "Name is required";
            }
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setServerMsg("");
        if (!validateForm()) {
            toast.error("Please correct the errors before submitting.");
            return;
        }
        setIsSubmitting(true);
        const payload = {
            ...primaryVisitor,
            groupMembers: groupMembers.map(m => ({
                name: m.name,
                phone: m.phone || null,
                type: m.type,
                hasVisited: m.hasVisited || false
            })),
            adultCount: parseInt(adultCount) || 0,
            childrenCount: 0
        };

        try {
            const response = await fetch(`${BASE_URL}/api/visitors/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.message || 'Something went wrong during check-in');

            // Show Thank You screen with group ID and visitor ID
            setRegisteredGroupId(data.groupId || data.visit?.groupId || "");
            setRegisteredVisitorId(data.visitor?.visitorId || "");
            setRegisteredName(data.visitor?.fullName || primaryVisitor?.fullName || "");
            sessionStorage.removeItem("tmgf_primary_visitor");
            setShowThankYou(true);

        } catch (err) {
            setServerMsg(err.message || "Something went wrong. Please check your connection and try again.");
            toast.error(err.message || "Submission failed.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const inputStyle = {
        padding: "12px", borderRadius: "8px", border: "1px solid #ddd",
        backgroundColor: "rgba(255, 255, 255, 0.9)", fontSize: "0.95rem"
    };

    const sectionTitleStyle = {
        color: "#ac2e62", fontSize: "1.1rem", fontWeight: "700",
        borderBottom: "2px solid #f0f0f0", paddingBottom: "8px",
        marginBottom: "20px", marginTop: "10px"
    };

    const bgStyle = {
        backgroundImage: "url('/images/Login.png')",
        backgroundSize: "cover",
        backgroundPosition: "right center",
        backgroundAttachment: "scroll",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        padding: "40px 0",
        fontFamily: "Inter, 'Segoe UI', Arial, sans-serif"
    };

    // ── Thank You Screen ──────────────────────────────────
    if (showThankYou) {
        return (
            <div style={bgStyle}>
                <Container>
                    <Row className="justify-content-center">
                        <Col md={8} lg={6} xl={5}>
                            <Card style={{
                                backgroundColor: "rgba(255,255,255,0.95)",
                                padding: "2.5rem 2rem",
                                borderRadius: "20px",
                                boxShadow: "0 15px 40px rgba(0,0,0,0.15)",
                                border: "none",
                                textAlign: "center"
                            }}>
                                <Link to="/"><img src="/images/LOGO.png" alt="TMGF" style={{ width: "160px", marginBottom: "20px" }} /></Link>

                                {/* Tick */}
                                <div style={{
                                    width: "80px", height: "80px", borderRadius: "50%",
                                    background: "linear-gradient(135deg, #2e7d32, #66bb6a)",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    margin: "0 auto 20px", boxShadow: "0 6px 20px rgba(46,125,50,0.35)"
                                }}>
                                    <span style={{ fontSize: "36px", color: "white" }}>✓</span>
                                </div>

                                <h2 style={{ color: "#2e7d32", fontWeight: "800", marginBottom: "8px" }}>
                                    Group Visit Logged{registeredName ? `, ${registeredName.split(" ")[0]}` : ""}!
                                </h2>
                                <p style={{ color: "#555", fontSize: "15px", marginBottom: "24px" }}>
                                    Your group visit has been successfully logged at TMGF.
                                </p>

                                {/* Visitor ID */}
                                {registeredVisitorId && (
                                    <div style={{
                                        background: "linear-gradient(135deg, #fdf2f6, #fce8f0)",
                                        border: "2px solid #ac2e62",
                                        borderRadius: "14px",
                                        padding: "16px",
                                        marginBottom: "14px"
                                    }}>
                                        <p style={{ fontSize: "11px", fontWeight: "700", color: "#888", textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: "6px" }}>
                                            Your Visitor ID
                                        </p>
                                        <p style={{ fontSize: "22px", fontWeight: "900", color: "#ac2e62", letterSpacing: "2px", marginBottom: "4px", fontFamily: "monospace" }}>
                                            {registeredVisitorId}
                                        </p>
                                        <p style={{ fontSize: "11px", color: "#888", marginBottom: "0" }}>Show this to NGO staff for verification</p>
                                    </div>
                                )}

                                {/* Group ID */}
                                {registeredGroupId && (
                                    <div style={{
                                        background: "linear-gradient(135deg, #f0f8ff, #e8f4fd)",
                                        border: "2px solid #2980b9",
                                        borderRadius: "14px",
                                        padding: "16px",
                                        marginBottom: "24px"
                                    }}>
                                        <p style={{ fontSize: "11px", fontWeight: "700", color: "#888", textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: "6px" }}>
                                            Group ID
                                        </p>
                                        <p style={{ fontSize: "18px", fontWeight: "900", color: "#2980b9", letterSpacing: "1px", marginBottom: "4px", fontFamily: "monospace" }}>
                                            {registeredGroupId}
                                        </p>
                                        <p style={{ fontSize: "11px", color: "#888", marginBottom: "0" }}>
                                            {groupMembers.length + 1} members in this group visit
                                        </p>
                                    </div>
                                )}

                                <Button
                                    onClick={() => navigate("/visitor/register")}
                                    style={{
                                        background: "#ac2e62", border: "none",
                                        borderRadius: "10px", padding: "12px 30px",
                                        fontWeight: "700", fontSize: "14px",
                                        color: "white", width: "100%",
                                        boxShadow: "0 4px 15px rgba(172,46,98,0.3)"
                                    }}
                                >
                                    ← Back to Home
                                </Button>
                            </Card>
                        </Col>
                    </Row>
                </Container>
                <ToastContainer />
            </div>
        );
    }

    if (!primaryVisitor) {
        return (
            <div className="d-flex align-items-center justify-content-center" style={{ minHeight: "100vh" }}>
                <Spinner animation="border" variant="danger" />
            </div>
        );
    }

    return (
        <div style={bgStyle}>
            <Container>
                <Row className="justify-content-center">
                    <Col md={8} lg={6} xl={5}>
                        <Card style={{
                            backgroundColor: "rgba(255, 255, 255, 0.92)",
                            padding: "2rem", borderRadius: "15px",
                            boxShadow: "0 10px 30px rgba(0,0,0,0.15)", border: "none"
                        }}>
                            <div className="text-center mb-4">
                                <Link to="/"><img src="/images/LOGO.png" alt="TMGF Logo" style={{ width: "180px", marginBottom: "15px" }} /></Link>
                                <span className="badge bg-secondary mb-2" style={{ fontSize: "0.85rem", padding: "6px 12px" }}>Step 2 of 2</span>
                                <h2 style={{ color: "#2c3e50", fontWeight: "800", letterSpacing: "-0.5px" }}>Group Member Details</h2>
                                <p className="text-muted">Enter contact details for group companions checking in with you.</p>
                            </div>

                            {/* Primary Visitor Banner */}
                            <div style={{
                                backgroundColor: "rgba(172, 46, 66, 0.05)",
                                border: "1px solid rgba(172, 46, 66, 0.15)",
                                borderLeft: "4px solid #ac2e62",
                                borderRadius: "8px", padding: "12px 16px", marginBottom: "24px"
                            }}>
                                <div style={{ fontSize: "11px", fontWeight: "700", color: "#ac2e62", textTransform: "uppercase", letterSpacing: "0.5px" }}>Primary Visitor</div>
                                <div style={{ fontSize: "15px", fontWeight: "700", color: "#2c3e50", marginTop: "2px" }}>{primaryVisitor.fullName || "Returning Visitor"}</div>
                                <div style={{ fontSize: "13px", color: "#666", marginTop: "1px" }}>{primaryVisitor.mobileNumber || primaryVisitor.identifier}</div>
                            </div>

                            {serverMsg && (
                                <Alert variant={serverMsg.includes("Success") ? "success" : "danger"} className="mb-4 text-center fw-bold">{serverMsg}</Alert>
                            )}

                            <Form onSubmit={handleSubmit}>
                                <div style={sectionTitleStyle}>👥 Group Details</div>
                                <Row className="mb-4 align-items-end">
                                    <Col md={8} xs={7}>
                                        <Form.Group>
                                            <Form.Label className="fw-bold small text-uppercase text-muted">Total Adult Members</Form.Label>
                                            <Form.Control
                                                type="number" min="0" placeholder="0"
                                                value={adultCount}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    setAdultCount(val);
                                                    const count = parseInt(val) || 0;
                                                    const currentAdults = groupMembers.filter(m => m.type === "adult");
                                                    if (count > currentAdults.length) {
                                                        const toAdd = count - currentAdults.length;
                                                        const newMembers = Array.from({ length: toAdd }, () => ({
                                                            id: Date.now() + Math.random(),
                                                            name: "", phone: "", type: "adult", hasVisited: false, lookupDone: false
                                                        }));
                                                        setGroupMembers(prev => [...prev, ...newMembers]);
                                                    } else if (count < currentAdults.length) {
                                                        setGroupMembers(currentAdults.slice(0, count));
                                                    }
                                                }}
                                                isInvalid={!!errors.adultCount}
                                                style={inputStyle}
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.adultCount}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={4} xs={5}>
                                        <Button type="button"
                                            onClick={() => {
                                                setGroupMembers(prev => [...prev, { id: Date.now() + Math.random(), name: "", phone: "", type: "adult", hasVisited: false, lookupDone: false }]);
                                                setAdultCount(prev => String((parseInt(prev) || 0) + 1));
                                            }}
                                            style={{ backgroundColor: "#ac2e62", border: "none", borderRadius: "8px", padding: "12px 10px", fontSize: "0.9rem", fontWeight: "700", color: "#fff", width: "100%" }}
                                        >+ Add Member</Button>
                                    </Col>
                                </Row>

                                {groupMembers.length > 0 && (
                                    <div style={{ maxHeight: "400px", overflowY: "auto", paddingRight: "5px", marginBottom: "20px" }}>
                                        {groupMembers.map((member, index) => (
                                            <div key={member.id} style={{ background: "white", border: "1px solid #eee", borderRadius: "10px", padding: "12px", marginBottom: "10px" }}>
                                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                                                    <span style={{ fontSize: "13px", fontWeight: "600", color: "#2980b9" }}>👤 Adult #{index + 1}</span>
                                                    <Button variant="link" size="sm" style={{ color: "#e74c3c", padding: 0, fontSize: "13px", textDecoration: "none" }} onClick={() => removeMember(member.id)}>✕ Remove</Button>
                                                </div>
                                                <Row>
                                                    <Col md={12} className="mb-2">
                                                        <Form.Group>
                                                            <Form.Label className="fw-bold small text-uppercase text-muted">Phone Number *</Form.Label>
                                                            <Form.Control
                                                                type="tel" placeholder="10-digit mobile"
                                                                value={member.phone}
                                                                onChange={(e) => {
                                                                    const val = e.target.value;
                                                                    if (val.length < 10) {
                                                                        setGroupMembers(prev => prev.map(m => m.id === member.id ? { ...m, phone: val, lookupDone: false, hasVisited: false, name: "" } : m));
                                                                    } else {
                                                                        updateMember(member.id, "phone", val);
                                                                        if (val.length === 10) checkGroupMemberPhone(member.id, val, index);
                                                                    }
                                                                }}
                                                                isInvalid={!!errors[`member_phone_${index}`]}
                                                                maxLength={10}
                                                                style={{ borderRadius: "8px", border: "1px solid #ddd", padding: "10px" }}
                                                            />
                                                            <Form.Control.Feedback type="invalid">{errors[`member_phone_${index}`]}</Form.Control.Feedback>
                                                        </Form.Group>
                                                    </Col>
                                                    {member.lookupDone && member.hasVisited && (
                                                        <Col md={12} className="mb-2">
                                                            <div style={{ fontSize: "13px", color: "#27ae60", fontWeight: "600", backgroundColor: "#e8f8f5", padding: "8px 12px", borderRadius: "6px", border: "1px solid #d1f2eb" }}>
                                                                ✓ Member {index + 1} has already visited us before
                                                            </div>
                                                        </Col>
                                                    )}
                                                    {member.lookupDone && !member.hasVisited && (
                                                        <Col md={12} className="mb-2">
                                                            <Form.Group>
                                                                <Form.Label className="fw-bold small text-uppercase text-muted">Name *</Form.Label>
                                                                <Form.Control
                                                                    type="text" placeholder="Full Name"
                                                                    value={member.name}
                                                                    onChange={(e) => updateMember(member.id, "name", e.target.value)}
                                                                    isInvalid={!!errors[`member_name_${index}`]}
                                                                    style={{ borderRadius: "8px", border: "1px solid #ddd", padding: "10px" }}
                                                                />
                                                                <Form.Control.Feedback type="invalid">{errors[`member_name_${index}`]}</Form.Control.Feedback>
                                                            </Form.Group>
                                                        </Col>
                                                    )}
                                                </Row>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div className="d-flex gap-3 mt-4">
                                    <Button variant="outline-secondary" type="button" className="w-100 py-3 fw-bold text-uppercase"
                                        onClick={() => navigate("/visitor/register")}
                                        style={{ borderRadius: "10px", letterSpacing: "1px" }}>
                                        Back to Step 1
                                    </Button>
                                    <Button className="w-100 py-3 fw-bold text-uppercase" type="submit" disabled={isSubmitting}
                                        style={{ background: isSubmitting ? "#bdc3c7" : "#ac2e62", border: "none", borderRadius: "10px", letterSpacing: "1px", boxShadow: isSubmitting ? "none" : "0 4px 15px rgba(172, 46, 98, 0.3)", cursor: isSubmitting ? "not-allowed" : "pointer" }}>
                                        {isSubmitting ? <Spinner animation="border" size="sm" /> : "Submit Group"}
                                    </Button>
                                </div>
                            </Form>
                        </Card>
                    </Col>
                </Row>
            </Container>
            <ToastContainer />
        </div>
    );
};

export default GroupDetails;